import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../design/brainTheme";
import { addStandupNote } from "../lib/api";
import { getAaronProjects, type BrainProject } from "../lib/brain-api";

const c = brainTokens.color;
const MAX = 500;

export function RaiseAtStandupDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState("");
  const [kind, setKind] = useState<"note" | "question">("note");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState<BrainProject[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    void getAaronProjects({ focusOnly: true })
      .then(setProjects)
      .catch(() => setProjects([]));
  }, [open]);

  // The project suffix rides inside the same 500-char note — shrink the visible budget
  // so the user can never type a note that fails on submit.
  const suffixLen = projectId ? `\n[project:${projectId}]`.length : 0;
  const maxText = MAX - suffixLen;

  const submit = async () => {
    const body = text.slice(0, maxText).trim();
    if (!body) return;
    const withProject = projectId ? `${body}\n[project:${projectId}]` : body;
    setBusy(true);
    try {
      await addStandupNote(withProject, kind);
      enqueueSnackbar("Queued for standup", { variant: "success" });
      setText("");
      setProjectId("");
      setKind("note");
      onClose();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to add note", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Raise at standup</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 0.5 }}>
          <ToggleButtonGroup exclusive size="small" value={kind} onChange={(_, v) => v && setKind(v)}>
            <ToggleButton value="note">Note</ToggleButton>
            <ToggleButton value="question">Question</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            select
            size="small"
            label="Project (optional)"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <MenuItem value="">None</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.projectId} value={p.projectId}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            autoFocus
            multiline
            minRows={3}
            fullWidth
            size="small"
            placeholder={kind === "question" ? "What do you need from the room?" : "What should we cover?"}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, maxText))}
            helperText={`${Math.min(text.length, maxText)}/${maxText}`}
            FormHelperTextProps={{ sx: { textAlign: "right", color: c.muted2 } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={busy || !text.trim()} onClick={() => void submit()}>
          Raise
        </Button>
      </DialogActions>
    </Dialog>
  );
}
